/*  
  Developers: Sakshi Bhise  
  File: AppReducer.tsx  
  Date: 06/18/2025  
  Project Name: AskHR  
  Team: DSET  
  
  Purpose:  
  Handles all state transitions for the AskHR global application state using a reducer pattern.  
  Receives actions and current state, then returns the new state based on action type and payload.  
  Centralizes updates for chat history, feedback, settings, user data, languages, and SqlDB health.  
*/

// ===================== Imports ===================== //  
// Import React and context hooks for global state management  
import { Conversation, SQLDBStatus, Feedback } from '../api';
import { Action, AppState } from './AppProvider';

// Define the reducer function to handle state updates based on dispatched actions  
export const appStateReducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    // Toggle the visibility of the chat history panel  
    case 'TOGGLE_CHAT_HISTORY':
      return { ...state, isChatHistoryOpen: !state.isChatHistoryOpen };

    // Update the currently active chat  
    case 'UPDATE_CURRENT_CHAT':
      return { ...state, currentChat: action.payload };

    // Update the loading state of the chat history (e.g., Loading, Success, Fail)  
    case 'UPDATE_CHAT_HISTORY_LOADING_STATE':
      return { ...state, chatHistoryLoadingState: action.payload };

    // Updates a chat entry in the chat history, or adds it if it doesn't exist  
    case 'UPDATE_CHAT_HISTORY':
      if (!state.chatHistory || !state.currentChat) {
        return state;
      }
      let conversationIndex = state.chatHistory.findIndex(
        (conv) => conv.id === action.payload.id
      );
      if (conversationIndex !== -1) {
        // If exists, update it  
        let updatedChatHistory = [...state.chatHistory];
        updatedChatHistory[conversationIndex] = state.currentChat;
        return { ...state, chatHistory: updatedChatHistory };
      } else {
        // If not exists, add it  
        return { ...state, chatHistory: [...state.chatHistory, action.payload] };
      }

    // Update the title of a specific chat in chat history  
    case 'UPDATE_CHAT_TITLE':
      if (!state.chatHistory) {
        return { ...state, chatHistory: [] };
      }
      let updatedChats = state.chatHistory.map((chat) => {
        if (chat.id === action.payload.id) {
          if (state.currentChat?.id === action.payload.id) {
            state.currentChat = { ...state.currentChat, title: action.payload.title };
          }
          return { ...chat, title: action.payload.title };
        }
        return chat;
      });
      return { ...state, chatHistory: updatedChats };

    // Delete a specific chat entry from history  
    case 'DELETE_CHAT_ENTRY':
      if (!state.chatHistory) {
        return { ...state, chatHistory: [] };
      }
      let filteredChat = state.chatHistory.filter((chat) => chat.id !== action.payload);
      state.currentChat = null;
      return { ...state, chatHistory: filteredChat };

    // Delete all chat history and reset current chat  
    case 'DELETE_CHAT_HISTORY':
      return { ...state, chatHistory: [], filteredChatHistory: [], currentChat: null };

    // Delete all messages from current chat  
    case 'DELETE_CURRENT_CHAT_MESSAGES':
      if (!state.currentChat || !state.chatHistory) {
        return state;
      }
      const updatedCurrentChat = {
        ...state.currentChat,
        messages: [],
      };
      return {
        ...state,
        currentChat: updatedCurrentChat,
      };

    // Update chat history with fetched data  
    case 'FETCH_CHAT_HISTORY':
      return { ...state, chatHistory: action.payload };

    // Update impersonation active flag with fetched data  
    case 'IS_IMPERSONATING':
      return { ...state, isImpersonating: action.payload };

    // Update SqlDB availability status  
    case 'SET_SQLDB_STATUS':
      return { ...state, isSqlDBAvailable: action.payload };

    // Update frontend settings  
    case 'FETCH_FRONTEND_SETTINGS':
      return { ...state, frontendSettings: action.payload };

    // Update user language
    case 'USER_LANGUAGE':
      return { ...state, userLanguage: action.payload };

    // Update user preferred language  
    case 'USER_PREFERRED_LANGUAGE':
      return { ...state, userPreferredLanguage: action.payload };

    // Update user impersonated  
    case 'IMPERSONATED_USER':
      return { ...state, ImpersonatedUser: action.payload, userPreferredLanguage: null };

    // Update user logged In  
    case 'LOGGED_IN_USER':
      return { ...state, loggedInUser: action.payload };

    // Update user logged In  
    case 'LOGGED_IN_USER_NAME':
      return { ...state, loggedInUserName: action.payload };

    // Update user GID  
    case 'USER_GID':
      return { ...state, userGId: action.payload };

    // Update user Employee group  
    case 'USER_EMPLOYEE_GROUP':
      return { ...state, userEmployeeGroup: action.payload };

    // Update user permission
    case 'USER_PERMISSION':
      return { ...state, userPermission: action.payload };

    // Update support user flag
    case 'IS_SUPPORT_USER':
      return { ...state, isSupportUser: action.payload };

    // Update the feedback state for a specific answer  
    case 'SET_FEEDBACK_STATE':
      return {
        ...state,
        feedbackState: {
          ...state.feedbackState,
          [action.payload.answerId]: action.payload.feedback,
        },
      };

    // Update the selected use case  
    case 'SET_SELECTED_USE_CASE':
      return {
        ...state,
        selectedUseCase: action.payload,
      };
    // Append a page of conversations to the existing chat history (load more / infinite scroll)
    case 'APPEND_CHAT_HISTORY':
      if (!state.chatHistory) {
        return { ...state, chatHistory: action.payload };
      }
      const existingIds = new Set(state.chatHistory.map((c) => c.id));
      const newEntries = action.payload.filter((c) => !existingIds.has(c.id));
      return { ...state, chatHistory: [...state.chatHistory, ...newEntries] };

    // Update the selected card  

    // Update the selected card  
    case 'SET_SELECTED_CARD':
      return {
        ...state,
        selectedCard: action.payload,
      };

    // Enables chat history (SqlDB working)  
    case 'ENABLE_CHAT_HISTORY':
      return {
        ...state,
        isSqlDBAvailable: {
          ...state.isSqlDBAvailable,
          sqlDB: true,
          status: SQLDBStatus.Working,
        },
      };

    // Disables chat history (SqlDB not configured)  
    case 'DISABLE_CHAT_HISTORY':
      return {
        ...state,
        isSqlDBAvailable: {
          ...state.isSqlDBAvailable,
          sqlDB: false,
          status: SQLDBStatus.NotConfigured,
        },
      };
    // Set available languages array 
    case "SET_AVAILABLE_LANGUAGES":
      return { ...state, availableLanguages: action.payload };

    case "SET_LOGGED_IN_USER_COUNTRY":
      return { ...state, loggedInUserCountry: action.payload }

    case "IMP_USER_COUNTRY":
      return { ...state, impUserCountry: action.payload }

    case 'TERMS_ACCEPTED_AT':
      return {
        ...state,
        termsAcceptedAt: action.payload
      };

    // Default fallback: return unchanged state  
    default:
      return state;
  }
};  